/* ================================================================ */
// MAP CONTAINER | Map.jsx
/* ================================================================ */
/* ========================================== */
// Imports
/* ========================================== */
import React, {useState, useRef, useEffect, useCallback} from 'react';
import PropTypes from 'prop-types';
// Styles
import 'mapbox-gl/dist/mapbox-gl.css';
// Configuration
//import JSON_CONFIGURATION from '@myglobe/core/dist/Map/configuration';
// Lodash
import {isEqual} from 'lodash';
// DeckGL
import DeckGL, {
    View,
    MapView,
    FirstPersonView,
    MapController,
    FlyToInterpolator,
    TRANSITION_EVENTS,
    COORDINATE_SYSTEM
} from 'deck.gl';
import GL from '@luma.gl/constants';
//import DeckGL, {FlyToInterpolator, TRANSITION_EVENTS} from '@deck.gl/react';
import {MapboxLayer} from '@deck.gl/mapbox';
import {JSONConverter} from '@deck.gl/json';
// React-Map-GL
import {
    Marker,
    StaticMap,
    Popup,
    _MapContext as MapContext
} from 'react-map-gl';
import {AttributionControl, ScaleControl} from 'mapbox-gl';
// Material UI
import IconButton from '@material-ui/core/Button';
import ResetIcon from '@material-ui/icons/SettingsBackupRestore';
import makeStyles from '@material-ui/styles/makeStyles';
import useTheme from '@material-ui/styles/useTheme';
// Hooks
import usePrevious from '@myglobe/core/dist/hooks/usePrevious';
// Local Component
import MyGlobeIcon from '@myglobe/core/dist/Branding/MyGlobeIcon';
import PopupHTML from '../Popup';

import {registerLoaders, _selectLoader, load} from '@loaders.gl/core';
import {CSVLoader} from '@loaders.gl/csv';

registerLoaders([CSVLoader]);
const JSON_CONFIGURATION = {
    //classes: Object.assign({}, require('@deck.gl/layers'), require('@deck.gl/aggregation-layers')),
    MapController,
    // a map of all layers that should be exposes as JSONLayers
    layers: {...require('@deck.gl/layers'), IconClusterLayer: require('./IconClusterLayer').default},
    // Any non-standard views
    views: {},
    // Enumerations that should be available to JSON parser
    // Will be resolved as `<enum-name>.<enum-value>`
    enumerations: {
        COORDINATE_SYSTEM,
        GL
    }
};
console.log('JSONCONFIG', JSON_CONFIGURATION);
/* ========================================== */
// Style Settings
/* ========================================== */
const useStyles = makeStyles(theme => ({
    root: {
        position: 'relative',
        backgroundColor: theme.palette.secondary.main,
        width: '100%',
        height: '100%'
    },
    resetButton: {
        position: 'absolute',
        right: '4em',
        bottom: '1em'
    },
    branding: {
        position: 'absolute',
        touchAction: 'none',
        zIndex: 1,
        right: '1rem',
        top: '1rem'
    }
}));
/* ========================================== */
// String Extension
/* ========================================== */
String.prototype.parseFunction = function () {
    var funcReg = /function *\(([^()]*)\)[ \n\t]*{(.*)}/gmi;
    var match = funcReg.exec(this.replace(/\n/g, ' '));
    if(match) {
        return new Function(match[1].split(','), match[2]);
    }
    return null;
};
const jsonConverter = new JSONConverter({configuration: JSON_CONFIGURATION});
console.log('jsonConverter', jsonConverter)
//TODO: Get these from map state OR globe config
const TRANSITION_SETTINGS = {
    transitionDuration: 2000,
    transitionEasing: x=>1-(1-x)*(1-x),
    transitionInterpolator: new FlyToInterpolator(),
    transitionInterruption: TRANSITION_EVENTS.IGNORE,
};
/* ========================================== */
/**
 * Map Component
 * -------------
 * Component for output of deck and mapbox
 * components with given vis layers and state
 *
 * @param {object} screen screen width and height
 * @param {string} mapboxToken mapbox api token
 * @param {string} mapStyle mapbox style uri
 * @param {object} viewState map's current viewState
 * @param {array} layers myglobe vis layers array
 * @param {function} onViewStateUpdate view state update callback
 * @param {function} onReset map state reset callback
 */
const MapComponent = (props) => {
    /* ======================== */
    // PROPS
    /* ======================== */
    const {
        // TODO: remove?
        //spriteJSON,
        //spriteImage,
        deckJsonConverterConfig,

        // MAP PROPS
        mapboxToken,
        mapStyle,

        // DECK PROPS
        deckLayers,

        // FUNCTIONS
        onViewStateUpdate,
        onReset,
    } = props;

    // JSON Converter
    //const [jsonConverter, setJsonConverter] = useState(new JSONConverter({configuration: deckJsonConverterConfig}));

    // Styles
    const classes = useStyles();
    const theme = useTheme();

    // Map Containers
    const mapboxContainer = useRef(null);
    const deckContainer = useRef(null);

    // Map Instances
    const [mapbox, setMapbox] = useState(null);
    const deck = useDeck(deckContainer);
    const [gl, setGL] = useState(null);

    // Map Style
    // TODO: remove?
    //const [currentMapStyle, setMapStyle] = useState(mapStyle);
    const previousMapStyle = usePrevious(mapStyle);
    const [styleLoaded, setStyleLoaded] = useState(false);

    // Map ViewState
    const [viewState, setViewState] = useState(props.viewState);

    // DeckGL
    // Deck Props
    const [deckProps, setDeckProps] = useState({});
    const prevDeckLayers = usePrevious(deckLayers);
    //const [deckLayers, setDeckLayers] = useState([]);

    // Mapbox
    // Potentially use redux/selector for these
    //const [mapboxLayers, setMapboxLayers] = useState(props.mapboxLayers);
    const previousMapboxLayers = usePrevious(props.mapboxLayers);

    // Map UI
    const [popup, setPopup] = useState(null);
    const [tooltip, setTooltip] = useState(null);
    const [markers, setMarkers] = useState(null);
    //const [ui, setUI] = useState(null);

    /* ======================== */
    // EFFECTS
    /* ======================== */
    /* ========================================== */
    /**
     * Effect: DeckGL Configuration Update
     * -----------------------------------
     * Creates a new deckgl JSONConverter using a provided
     * converter configuration
     */
    /*useEffect(()=>{
        console.debug(`${MapComponent.displayName} | `, 'Effect:Update (jsonConverter) -', 'deckJsonConverterConfig:', deckJsonConverterConfig);
        const newConverter = new JSONConverter({configuration: deckJsonConverterConfig});
        setJsonConverter(newConverter);
    }, [deckJsonConverterConfig]);*/
    /* ========================================== */
    /**
     * Effect: Deck Layer Update
     * -------------------------
     * Creates new deck props json configuration
     * based on the incoming deckLayers prop
     */
    useEffect(()=>{
        console.debug(`${MapComponent.displayName} | `, 'Effect:Update (deckLayers) -', 'prevDeckLayers', prevDeckLayers, 'deckLayers:', deckLayers);
        let isSubscribed = true;
        // Only update decklayers when mismatch between incoming props and state
        if(deck.current/* && !isEqual(prevDeckLayers, deckLayers)*/) {
            console.debug(`${MapComponent.displayName} | `, 'Effect:Update (deckLayers) -', 'deckLayers changed');
            formatDeckConfig(deckLayers, /*jsonConverter,*/ viewState, setViewState, setPopup).then(dP=>{
                console.debug(`${MapComponent.displayName} | `, 'Effect:Update (deckLayers) -', 'deckProps:', dP);
                isSubscribed && setDeckProps(dP);
            });
        }
        return () => isSubscribed = false;
    }, [deck, prevDeckLayers, deckLayers/*, props.viewState*/]);

    // ATTEMPT AT REBUILDING LAYERS FOR IN LAYER FUNCTION VIEWSTATE CONTEXT
    /*useEffect(()=>{
        if(deck) {
            console.log(deck);
            console.log(viewState);
            const layers = deck.current.layerManager?.lastRenderedLayers.map(layer=>new layer.constructor(layer.props));
            console.log(layers);
            if(layers?.length > 0) {
                const dP = {
                    ...deckProps,
                    layers
                };
                console.log(dP);
                setDeckProps(dP);
            }
        }
    }, [deck, viewState, deckProps]);*/

    /* ========================================== */
    /**
     * Effect: Map State Update
     * ------------------------
     * Handles incoming view state prop transitions
     * and layer updates
     */
    useEffect(()=>{
        /* ======================== */
        // COMPONENT UPDATE
        /* ======================== */
        if(props.viewState.longitude !== viewState.longitude
            || props.viewState.latitude !== viewState.latitude
            || props.viewState.zoom !== viewState.zoom
            || props.viewState.bearing !== viewState.bearing
            || props.viewState.pitch !== viewState.pitch) {

            console.debug(`${MapComponent.displayName} | `, 'Update(viewState) -', 'props.viewState (new):', props.viewState, 'viewState (old):', viewState);

            setViewState({
                ...viewState,
                ...props.viewState,
                ...TRANSITION_SETTINGS,
                onTransitionStart: handleLayers
            });
            //onViewStateUpdate(props.viewState);
        }

        // Layers Update
        /*if(deckLayers !== props.deckLayers || mapboxLayers !== props.mapboxLayers) {
            setDeckLayers(props.deckLayers);
            //handleMapboxLayers(props.mapboxLayers);
        }*/

        if (styleLoaded&&mapStyle && previousMapStyle && mapStyle.id != previousMapStyle.id) {
            console.debug(`${MapComponent.displayName} | `, 'Update(mapStyle) -', 'mapstyle:', mapStyle, 'previousMapStyle:', previousMapStyle);
            handleLayers();
        }

        /*if(previousMapboxLayers !== mapboxLayers) {
            handleLayers();
        }*/
    }, [mapbox, deck, props.viewState, props.mapboxLayers, props.mapStyle]);


    /* ========================================== */
    /**
     * Handle Layers
     * -------------
     * Toggles mapbox layer visibility according
     * to the list loaded style layers and provided
     * mapbox layers for the current map state
     */
    function handleLayers() {
        setPopup(null);
        if(mapbox) {
            // Hide non-base mapbox layers
            const loadedStyleLayers = (props.mapStyle.layers) ? props.mapStyle.layers.filter(layer=>!layer.metadata).map(layer=>layer.id) : [];
            console.debug(`${MapComponent.displayName} | `, 'Handle Layers (mapboxLayers) -', 'Incoming mapboxLayers', props.mapboxLayers, 'Loaded styleLayers:', loadedStyleLayers);
            // Hide Loaded Layers
            loadedStyleLayers.forEach(layer=>mapbox.setLayoutProperty(layer,'visibility','none'));
            props.mapboxLayers.forEach(layer=>mapbox.setLayoutProperty(layer.name,'visibility','visible'));
        }
    }
    /* ======================== */
    // HELPERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle WebGL Context
     * --------------------
     * Stores webgl instance created from deckgl to
     * use for mapbox static map
     *
     * @param {object} gl webgl instance
     */
    function handleWebGLInit(gl) {
        // DeckGL and mapbox will both draw into this WebGL context
        console.debug(`${MapComponent.displayName} | `, 'handleWebGLInit -', 'gl:', gl);
        setGL(gl);
    }
    /* ======================== */
    // LOAD HANDLERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle Load
     * -----------
     * Handles the initial load of the interactive
     * map component (mapbox) and sets the default controls
     * and layers
     */
    function handleLoad() {
        if(mapboxContainer.current) {
            const mapboxInstance = mapboxContainer.current.getMap();
            mapboxInstance.addControl(new AttributionControl({compact: true}), 'bottom-right');
            mapboxInstance.addControl(new ScaleControl({unit: 'metric'}), 'bottom-right');
            mapboxInstance.on('styledata', ()=>{
                setStyleLoaded(true);
            });
            //mapboxInstance.on('click', handleClick);
            console.debug(`${MapComponent.displayName} | `, 'handleLoad -', 'mapbox:', mapboxInstance);
            setMapbox(mapboxInstance);
        }
    }
    /* ======================== */
    // LAYER HANDLERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle Mapbox Layers
     * --------------------
     * Updates mapbox layers with the new specified layers
     * via the mapbox api and mapboxLayers state
     *
     * @param {array} layers new mapbox layers to apply
     */
    /*function handleMapboxLayers(layers) {
        if(mapbox) {
            // Mapbox layer Ids
            var mapboxLayerIDs = [];
            // Remove already loaded mapbox layers from mapbox
            mapboxLayers.map(layer=>{
                if (mapbox.getLayer(layer))
                    mapbox.removeLayer(layer);
                if (mapbox.getSource(layer))
                    mapbox.removeSource(layer);
            });

            // Add new layers to mapbox
            layers.map( layer => {
                mapboxLayerIDs.push(layer.id);
                mapbox.addLayer(layer, layer.id);
            });
            // Store loaded mapbox layer ids
            setMapboxLayers(mapboxLayerIDs);
        }
    }*/
    /* ======================== */
    // EVENT HANDLERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle View Update
     * ------------------
     * Determines viewState change from map component
     * callback props
     *
     * @param {object} states bundled states including
     * interaction state, old view state, and current view state
     */
    function handleViewUpdate({viewState, interactionState, oldViewState}) {
        //console.debug(`${MapComponent.displayName} | `, 'handleViewUpdate -', 'viewState:', viewState, 'interactionState:', interactionState, 'oldViewState:', oldViewState);
        // Set view state
        setViewState(viewState);
        // Update active popup
        if(popup) {
            const coords = mapbox?mapbox.project(popup.anchor):null;
            setPopup({...popup, coords});
        }
        if(oldViewState && !Object.values(interactionState).includes(true)) {
            // Check for last view state and all interactions are complete
            if(Object.keys(viewState).map(key=>oldViewState[key] === viewState[key]).includes(false)) {
                // Update detected
                if(onViewStateUpdate)
                    onViewStateUpdate(viewState);
            }
        }
        return viewState;
    }
    /* ========================================== */
    /**
     * Handle Hover
     * ------------
     * Handles layer's hover interaction event
     *
     * @param {object} obj deck object calling hover
     */
    /*function handleHover(obj) {
        if (obj) {
            var message = '';
            if (obj.sourceLayer) {
                for (var i = 0; i < this.deckProps.layers[0].props.data.length; i++) {
                    if (this.deckProps.layers[0].props.data[i].id == obj.sourceLayer.id){
                        if (this.deckProps.layers[0].props.data[i].onHover) {
                            var func = this.deckProps.layers[0].props.data[i].onHover.parseFunction();
                            message = func(obj);
                        }
                    }
                }
            } else {
                // No function message defined
                message = '';
            }

            // Set tooltip at coord
            let x = obj.x;
            let y = obj.y;
            setTooltip({
                message,
                x,
                y
            });
        } else {
            if (tooltip) {
                // Remove tooltip
                setTooltip(null);
            }
        }
    }*/
    /* ========================================== */
    /**
     * Handle Click
     * ------------
     * Handles map click event
     *
     * @param {object} object clicked object data
     * @param {event} event scroll wheel event
     */
    function handleClick(pointerObj, e) {
        console.debug(`${MapComponent.displayName} | `, 'handleClick -', 'pointerObj:', pointerObj);
    }
    /* ========================================== */
    /**
     * Handle Popup Close
     * ------------------
     * Closes popup by removing the popup state
     */
    function handlePopupClose() {
        if(deck) {
            // Close open popup
            if(popup)
                setPopup(null);
        }
    }
    /* ========================================== */
    /**
     * Handle Reset Button
     * -------------------
     * Triggers onReset property function
     */
    const handleResetButton = useCallback(() => {
        onReset();
    }, [onReset]);
    /* ======================== */
    // RENDER
    /* ======================== */
    /* ========================================== */
    /**
     * Render Base UI
     * --------------
     * Renders branding and base map control buttons
     */
    function renderUI() {
        return (
            <>
                <IconButton
                    key="btn_reset"
                    className={classes.resetButton}
                    onClick={handleResetButton}
                >
                    <ResetIcon />
                </IconButton>
                <div className={classes.branding}>
                    <MyGlobeIcon type={theme.palette.type} />
                </div>
            </>
        );
    }
    /* ========================================== */
    /**
     * Render Children
     * ---------------
     * Exposes mapbox and deck instances to a
     * child function wrapper
     */
    function renderChildren() {
        if (typeof props.children === 'function') {
            return (
                <div>
                    {props.children(mapbox, deck.current)}
                </div>
            );
        }
    }
    return (
        <>
            <div
                key="map"
                className={classes.root + ' mapbox-container'}
                onContextMenu={event => event.preventDefault()}
            >
                <DeckGL
                    ContextProvider={MapContext.Provider}
                    ref={deckContainer}
                    {...deckProps}
                    views={[
                        new MapView({id: 'main', width: '100%', height: '100%', controller: true}),
                        //new FirstPersonView({id: 'firstperson', width:200, height: 200, controller: false, viewState: {id: 'main', pitch: 0, zoom: 10}})
                    ]}
                    viewState={viewState}
                    onViewStateChange={handleViewUpdate}
                    onWebGLInitialized={handleWebGLInit}
                    onClick={handleClick}
                    //onHover={handleHover}
                    parameters={{
                        blendFunc: [GL.SRC_ALPHA, GL.ONE, GL.ONE_MINUS_DST_ALPHA, GL.ONE],
                        blendEquation: GL.FUNC_ADD
                    }}
                >
                    {popup && (
                        <Popup
                            latitude={popup.anchor.lat}
                            longitude={popup.anchor.lng}
                            anchor="bottom"
                            dynamicPosition={false}
                            sortByDepth={true}
                            closeOnClick={true}
                            closeButton={false}
                            onClose={handlePopupClose}
                        >
                            <PopupHTML {...popup} />
                        </Popup>
                    )}
                    <Markers markers={markers} />
                    { renderChildren() }
                    <View id="main">
                        <StaticMap
                            key="static-map"
                            attributionControl={false}
                            ref={mapboxContainer}
                            //gl={gl}
                            mapStyle={mapStyle}//currentMapStyle}
                            mapboxApiAccessToken={mapboxToken}
                            onLoad={handleLoad}
                            reuseMaps={false}
                            dragToRotate={false}
                        />
                    </View>
                    {//<View id="firstperson" width="100%" x="100%" fovy={50} />
                        }
                    <Tooltip tooltip={tooltip} />
                </DeckGL>
            </div>
            { renderUI() }
        </>
    );
};
/* ======================== */
// PROPS
/* ======================== */
MapComponent.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.elementType,
        PropTypes.func
    ]),
    screen: PropTypes.shape({
        width: PropTypes.number.isRequired,
        height: PropTypes.number.isRequired
    }),
    // Map Props
    mapboxToken: PropTypes.string.isRequired,
    mapStyle: PropTypes.string.isRequired,
    viewState: PropTypes.shape({
        longitude: PropTypes.number.isRequired,
        latitude: PropTypes.number.isRequired,
        zoom: PropTypes.number.isRequired,
        pitch: PropTypes.number.isRequired,
        bearing: PropTypes.number.isRequired
    }),
    // Vis Layers
    deckLayers: PropTypes.arrayOf(PropTypes.object),
    deckJsonConverterConfig: PropTypes.arrayOf(PropTypes.object),
    //mapboxLayers: PropTypes.arrayOf(PropTypes.object),
    mapboxLayers: PropTypes.arrayOf(PropTypes.string),
    markers: PropTypes.arrayOf(PropTypes.object),
    spriteJSON: PropTypes.object,
    // Functions
    onViewStateUpdate: PropTypes.func,
    onReset: PropTypes.func
};
MapComponent.defaultProps = {
    deckLayers: [],
    mapboxLayers: [],
    markers: []
};
MapComponent.displayName = 'MapComponent';
/* ======================== */
// HOOKS
/* ======================== */
/* ========================================== */
/**
 * Use Mapbox
 * ----------
 * Gets mapbox instance from mapbox component
 * reference
 *
 * @param {reference} mapboxRef reference to mapbox component
 */
const useMapbox = (mapboxRef) => {
    const mapboxInstance = useRef(null);
    const currentRef = mapboxRef.current;
    useEffect(()=>{
        if(currentRef)
            mapboxInstance.current = mapboxRef.current.getMap();
    }, [mapboxRef, currentRef]);
    return mapboxInstance;
};
/* ========================================== */
/**
 * Use Deck
 * ---------
 * Gets deckgl instance from deck component
 *
 * @param {reference} deckRef reference to deckgl component
 */
const useDeck = (deckRef) => {
    const deckInstance = useRef(null);
    useEffect(()=>{
        if(deckRef.current)
            deckInstance.current = deckRef.current.deck;
    }, [deckRef]);
    return deckInstance;
};

/* ========================================== */
/**
 * Markers
 * -------
 * Renders map markers
 *
 * @param {array} markers markers data
 */
const Markers = ({markers}) => {
    /* ======================== */
    // PROPS
    /* ======================== */
    //const classes = useStyles();
    /* ======================== */
    // HANDLERS
    /* ======================== */
    /* ========================================== */
    /**
     * Handle Click
     * ------------
     * Outputs data for marker click
     * @param {object} data marker data prop
     */
    function handleClick(data) {
        console.debug(`${Markers.displayName} | `, 'handleClick -', 'data:', data);
    }
    /* ======================== */
    // RENDER
    /* ======================== */
    return markers && markers.data.map((marker, index) => {
        // create a DOM element for the marker
        return (
            <Marker
                captureDrag={false}
                key={'markerID'+index}
                latitude={marker.geometry.coordinates[1]}
                longitude={marker.geometry.coordinates[0]}
                offsetLeft={markers.anchor.left*markers.scale}
                offsetTop={markers.anchor.top*markers.scale}
            >
                <div
                    style={{pointerEvents: 'all', transform: `scale(${markers.scale})`}}
                    //onWheel={handleWheel}
                    //onClick={handleClick(this, marker.properties)}
                    //dangerouslySetInnerHTML={{__html: LGAQPin(marker.properties[markers.dataChannels[0]]||0,marker.properties[markers.dataChannels[1]]||0)}}
                />
            </Marker>
        );
    });
};
/* ========================================== */
/**
 * Tooltip
 * -------
 * Renders map tooltip
 */
const Tooltip = (props) => {
    //const classes = useStyles();
    const {tooltip} = props;
    /* ======================== */
    // RENDER
    /* ======================== */
    return tooltip && tooltip.message && (
        <div
            style={{position: 'absolute', zIndex: 1, pointerEvents: 'none', left: tooltip.x, top: tooltip.y}}
        >
            { tooltip.message }
        </div>
    );
};
/* ========================================== */
// Export
/* ========================================== */
export default MapComponent;
/*export default React.memo(MapComponent, areEqual);
function areEqual(prevProps, nextProps) {
    return (
        prevProps.mapStyle === nextProps.mapStyle &&
        !prevProps.mapboxLayers.some((layer, i)=>layer !== nextProps.mapboxLayers[i]) &&
        !prevProps.loadedLayers.some((layer, i)=>layer !== nextProps.loadedLayers[i]) &&
        !prevProps.deckLayers.some((layer, i)=>layer !== nextProps.deckLayers[i]) &&
        JSON.stringify(prevProps.viewState) === JSON.stringify(nextProps.viewState)
    );
}*/
/* ========================================== */
/**
 * Format Deck Config
 * ------------------
 * Transforms deck configuration from json format
 * including resolving any functions with current
 * map component functions
 * @param {array} deckLayers deck layers
 * @param {object} viewState current map view state
 * @param {func} setPopup sets current popup UIs
 */
const formatDeckConfig = (
    deckLayers,
    //jsonConverter,
    // eslint-disable-next-line no-unused-vars
    viewState,
    // eslint-disable-next-line no-unused-vars
    setViewState,
    setPopup
) => new Promise((resolve, reject) => {
    try {
        // eslint-disable-next-line no-unused-vars
        const zoom = (viewState, lng, lat) => {
            setViewState({...viewState, longitude: lng, latitude: lat, zoom: (viewState.zoom + (4*Math.log10(viewState.zoom))), ...TRANSITION_SETTINGS});
        };
        // eslint-disable-next-line no-unused-vars
        const openPopup = (x, y, alng, alat, title, image, contact, summary) => {
            setPopup({
                coords:{x, y},
                //anchor: [alat,alng]
                anchor:{ lng: alng, lat: alat},
                content:{title, image, contact, summary}
            });
        };

        const deckLayerConfigs = deckLayers.map(deckLayer=>{
            let config = {};
            Object.keys(deckLayer.config).forEach(key=>{
                const value = deckLayer.config[key];
                //if(key !== 'updateTriggers') {
                config[key] = (/(\w+\s?=>[^\n]+)|(function\([\w\s\,]+\)\s?\{[^\n]+\})/.test(value)) ? eval(`(${value})`) : value;
                /*} else {
                    let updateTriggers = {};
                    Object.keys(value).forEach(triggerKey=>{
                        updateTriggers[triggerKey] = value[triggerKey].map(val=>eval(`(${val})`));
                    });
                    config['updateTriggers'] = updateTriggers;
                }*/
            });

            // Parse interaction handler functions
            /*Object.keys(deckLayer.config).filter(key=>(/on\w+/).test(key)).forEach(key=>{
                const value = deckLayer.config[key];
                if(value && typeof value === 'object') {
                    const handlerFunction = d => Object.keys(value).map(funcName=>functionLibrary[funcName](value[funcName]))
                    config[key] = handlerFunction;//functionLibrary[value]
                }
            })*/
            console.debug(`${MapComponent.displayName} | `, 'formatDeckConfig -', 'config:', config);
            return config;
        });

        // Resolve DeckProps
        const dP = jsonConverter.convert({
            controller: 'MapController',
            //mapStyle: typeof mapStyle === 'string' ? mapStyle : `mapbox://styles/ifeviser/${mapStyle.id}`,
            layers: [...deckLayerConfigs]
        });
        console.debug(`${MapComponent.displayName} | `, 'formatDeckConfig -', 'deckProps:', dP);
        resolve(dP);

    } catch (e) {
        console.error(`${MapComponent.displayName} | `, 'formatDeckConfig -', e);
        reject(e);
    }
});